import { FtIn } from './ft-in.js';
import { parse as dslParse, evaluate as dslEvaluate } from './dsl.js';
import { getBendType } from './bend-catalog.js';
const toRadians = (deg) => (deg * Math.PI) / 180;
/**
 * ACI 318-19 standard-hook MBD multiplier for pin-radius/pin-diameter
 * schedule: 6·db for #3-#8, 8·db for #9-#11, 10·db for #14 and #18.
 */
export function aciDefaultMbdMultiplier(barSize) {
    const s = barSize.imperial;
    if (s <= 8)
        return 6;
    if (s <= 11)
        return 8;
    return 10;
}
// Shear 97 hook-code macros map to canonical catalog entries.
// S180 → CUSTOM-HOOK-180 is known-broken — see docs/phase3-crsi-reconciliation.md.
const HOOK_CODE_MAP = {
    S90: 'Type8',
    S135: 'CUSTOM-HOOK-135',
    S180: 'CUSTOM-HOOK-180',
};
/**
 * Resolve the full set of dimension values for a bend type, given
 * user-supplied dimensions and the bar size (needed for code-prescribed
 * formulas like `12*db`).
 *
 * For each dimension in the bend type's spec:
 *   - source === 'user': require the value from `userDimensions` when the
 *     spec is `required`; otherwise pass through if provided.
 *   - source === 'code': if the user supplied a value, it overrides the
 *     code default; otherwise evaluate the `defaultFormula` via the DSL.
 */
export function resolveDimensions(bendType, userDimensions, barSize) {
    const resolved = {};
    for (const dim of bendType.dimensions) {
        if (dim.source === 'user') {
            const provided = userDimensions[dim.label];
            if (provided === undefined) {
                if (dim.required) {
                    throw new Error(`Bend ${bendType.code}: required dimension ${dim.label} not provided`);
                }
                continue;
            }
            resolved[dim.label] = provided;
        }
        else {
            // source === 'code'
            // If user provided a value, allow override. Otherwise evaluate defaultFormula.
            if (userDimensions[dim.label] !== undefined) {
                resolved[dim.label] = userDimensions[dim.label];
                continue;
            }
            if (!dim.defaultFormula) {
                throw new Error(`Bend ${bendType.code}: code-sourced dimension ${dim.label} missing defaultFormula`);
            }
            // TODO(Task 12+): when a defaultFormula references another dimension label,
            // we'll need to resolve dims in topological order and pass resolved via
            // ctx.dimensions. Current catalog formulas only reference `db`, so a single
            // pass with bare ctx is safe.
            const ctx = { barSize };
            resolved[dim.label] = dslEvaluate(dslParse(dim.defaultFormula), ctx);
        }
    }
    return resolved;
}
/**
 * Compute the bend deduction for the nth bend segment of a bend type.
 *
 * Uses the ACI 318-19 tangent-method deduction: 2r·tan(θ/2) − r·θ,
 * where r is pin radius from the standard-hook MBD schedule.
 *
 * Honors per-type `deductionOverrides["{imperialSize}_{bendIndex}"]` when present.
 *
 * NOTE: 180° bends throw — the tangent formula's sharp-corner convention is
 * geometrically undefined at θ=180° (legs parallel, no corner). CUSTOM-HOOK-180
 * relies on this function via its lengthFormula, so its lengthFormula is
 * known-broken until either a deductionOverrides entry lands for it or the
 * formula is redesigned. Tracked in docs/phase3-crsi-reconciliation.md.
 */
export function computeBendDeduction(bendType, barSize, bendIndex) {
    // Check per-type override first: key is "imperialSize_bendIndex" (e.g., "5_0").
    const overrideKey = `${barSize.imperial}_${bendIndex}`;
    const override = bendType.deductionOverrides?.[overrideKey];
    if (override !== undefined) {
        return FtIn.parse(override);
    }
    // Locate the nth bend segment in the geometry.
    const bendSegments = bendType.geometry.filter(s => s.type === 'bend');
    const seg = bendSegments[bendIndex];
    if (!seg || seg.angle === undefined) {
        throw new Error(`Bend ${bendType.code}: no bend at index ${bendIndex}`);
    }
    if (seg.angle === 180) {
        throw new Error(`Bend ${bendType.code}: 180° deduction undefined under sharp-corner convention. ` +
            `Use deductionOverrides or redesign lengthFormula (see docs/phase3-crsi-reconciliation.md).`);
    }
    // Determine MBD multiplier per ACI 318-19 (standard-hook schedule).
    const mbdMultiplier = aciDefaultMbdMultiplier(barSize);
    const db = barSize.nominalDiameter; // inches
    const pinRadius = (mbdMultiplier * db) / 2; // inches
    const thetaRad = toRadians(seg.angle);
    const deductionInches = 2 * pinRadius * Math.tan(thetaRad / 2) - pinRadius * thetaRad;
    // Formula is provably non-negative on θ ∈ (0°, 180°); 180° is thrown above.
    return FtIn.fromDecimalInches(deductionInches);
}
/**
 * Compute the theoretical (out-to-out) cut length of a bar for a given bend type,
 * resolved dimensions, and bar size, by evaluating the bend type's `lengthFormula`
 * through the DSL. The `deduction(i)` call is wired to `computeBendDeduction` so
 * the formula transparently pulls the correct per-bend reduction.
 *
 * Bend types whose `lengthFormula` is the `"special"` sentinel (e.g. continuous
 * spirals) are not DSL-driven and must be handled by dedicated code paths; this
 * function throws for them. Note this is distinct from `family === 'special'`:
 * the CUSTOM-USER-DEFINED escape hatch lives in the `special` family but has a
 * trivially DSL-evaluable `lengthFormula: "A"`, so it is handled here normally.
 */
export function computeTheoreticalLength(bendType, resolvedDimensions, barSize) {
    if (bendType.lengthFormula === 'special') {
        throw new Error(`Bend ${bendType.code}: lengthFormula="special" requires dedicated handler, not DSL`);
    }
    const ctx = {
        barSize,
        dimensions: resolvedDimensions,
        lookupDeduction: (idx) => computeBendDeduction(bendType, barSize, idx),
    };
    const result = dslEvaluate(dslParse(bendType.lengthFormula), ctx);
    if (result.toDecimalFeet() <= 0) {
        throw new Error(`Bend ${bendType.code}: computed non-positive theoretical length (got ${result.toString()} from formula "${bendType.lengthFormula}")`);
    }
    return result;
}
// Hook-adder resolver for Shear 97 S90 / S135 / S180 macros.
//
// The adder length = hook-extension B − deduction0 (same as the hook's own
// lengthFormula contribution when A=0). S180 currently throws at resolution
// time because (a) CUSTOM-HOOK-180's B defaultFormula uses `max()` which the
// DSL doesn't implement, and (b) computeBendDeduction throws on 180° bends.
// That's acceptable — S180 is not tested here and the failure mode is loud.
function hookAdderLength(hookCode, barSize) {
    const code = HOOK_CODE_MAP[hookCode];
    if (!code)
        throw new Error(`Unknown hook code ${hookCode}`);
    const bt = getBendType(code);
    const bDim = bt.dimensions.find((d) => d.label === 'B');
    if (!bDim?.defaultFormula)
        throw new Error(`${code} missing B defaultFormula`);
    const bLen = dslEvaluate(dslParse(bDim.defaultFormula), { barSize });
    const deduction = computeBendDeduction(bt, barSize, 0);
    return bLen.subtract(deduction);
}
/**
 * Public API for evaluating a formula string against a bar size and optional
 * dimension bindings. This is the agent-facing entry point for Shear 97 macro
 * strings (e.g. `S90 + 1.33 * [23-6] + 48 * db`); it wires in the hook-adder
 * resolver so `S90 / S135 / S180` identifiers resolve to catalog-driven lengths.
 *
 * The underlying DSL module is not publicly exported — `evaluateFormula` is the
 * only supported external path.
 */
export function evaluateFormula(formula, context) {
    const ctx = {
        barSize: context.barSize,
        dimensions: context.dimensions,
        lookupHookAdder: (hookCode, size) => hookAdderLength(hookCode, size),
    };
    return dslEvaluate(dslParse(formula), ctx);
}
